import React, { useEffect, useState } from "react";
import { Image, Platform, Pressable, Text, View } from "react-native";
import { STICKER_BY_ID } from "@/src/constants/stickers";
import type { StickerPlacement } from "@/src/services/api/posts/engagement-functions";
import { stickerPx } from "@/src/components/posts/stickers/sticker-geometry";

export type Box = { left: number; top: number; w: number; h: number };

type Props = Readonly<{
  s: Pick<StickerPlacement, "stickerId" | "x" | "y" | "rotation" | "scale">;
  box: Box;
  label: string;
  testID?: string;
  opacity?: number;
  /** Extra tap handling (sticker mode uses it to focus the sticker). */
  onPress?: () => void;
}>;

const TOOLTIP_MS = 2500;

/**
 * One placed sticker at its fractional position in `box`, with a tooltip naming
 * who added it: on hover (web/desktop), and on long-press or tap (touch).
 */
const PlacedSticker = ({ s, box, label, testID, opacity = 1, onPress }: Props) => {
  const def = STICKER_BY_ID[s.stickerId];
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if (!pinned) return;
    const id = setTimeout(() => setPinned(false), TOOLTIP_MS);
    return () => clearTimeout(id);
  }, [pinned]);
  if (!def) return null;
  const px = stickerPx(box, s.scale);
  const show = hover || pinned;
  // Near the top edge the bubble goes below the sticker so the card does not clip it.
  const below = s.y * box.h - px / 2 < 28;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="image"
      accessibilityLabel={label}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      onLongPress={() => setPinned(true)}
      onPress={() => { setPinned((p) => !p); onPress?.(); }}
      style={{
        position: "absolute",
        left: s.x * box.w - px / 2,
        top: s.y * box.h - px / 2,
        width: px,
        height: px,
        opacity,
        zIndex: show ? 20 : 1,
        transform: [{ rotate: `${s.rotation}deg` }],
        ...(Platform.OS === "web" ? ({ cursor: "default", userSelect: "none" } as object) : {}),
      }}
    >
      <Image source={def.source} style={{ width: px, height: px }} accessibilityLabel={def.label} {...({ draggable: false } as any)} />
      {show ? (
        <View
          pointerEvents="none"
          testID="sticker-tooltip"
          style={{
            position: "absolute",
            left: px / 2,
            ...(below ? { top: px + 4 } : { bottom: px + 4 }),
            // Undo the sticker's rotation so the text reads straight.
            transform: [{ translateX: "-50%" as any }, { rotate: `${-s.rotation}deg` }],
          }}
        >
          <View style={{ maxWidth: 220, backgroundColor: "rgba(17,24,39,0.9)", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 }}>
            <Text style={{ color: "#fff", fontSize: 12 }} numberOfLines={1}>
              {label}
            </Text>
          </View>
        </View>
      ) : null}
    </Pressable>
  );
};

export default PlacedSticker;
